import Alpine from "alpinejs";
import persist from "@alpinejs/persist";
import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

Alpine.plugin(persist);

const preferredDarkMode = () => window.matchMedia("(prefers-color-scheme: dark)").matches;

const datepickerPrevArrow =
  '<svg class="stroke-current" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.25 6L9 12.25L15.25 18.5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const datepickerNextArrow =
  '<svg class="stroke-current" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.75 19L15 12.75L8.75 6.5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const initializeDarkMode = (scope) => {
  document.documentElement.classList.remove("no-js");

  if (localStorage.getItem("dashboard.darkMode") === null) {
    scope.darkMode = preferredDarkMode();
  }

  scope.$watch("darkMode", (value) => {
    document.documentElement.classList.toggle("dark", value);
  });

  document.documentElement.classList.toggle("dark", scope.darkMode);
};

const initializeDatepickers = () => {
  flatpickr("[data-datepicker='br']", {
    allowInput: true,
    dateFormat: "d/m/Y",
    locale: Portuguese,
    monthSelectorType: "static",
    nextArrow: datepickerNextArrow,
    prevArrow: datepickerPrevArrow,
    static: true,
  });
};

Alpine.data("dashboardShell", () => ({
  darkMode: Alpine.$persist(false).as("dashboard.darkMode"),
  sidebarToggle: false,

  init() {
    initializeDarkMode(this);
    this.$nextTick(() => initializeDatepickers());
  },
}));

Alpine.data("authShell", () => ({
  darkMode: Alpine.$persist(false).as("dashboard.darkMode"),

  init() {
    initializeDarkMode(this);
  },
}));

Alpine.data("templateMessageForm", ({ templatesElementId }) => ({
  selectedTemplateId: "",
  variableValues: "",
  templates: [],

  init() {
    const templatesElement = document.getElementById(templatesElementId);
    this.templates = templatesElement ? JSON.parse(templatesElement.textContent) : [];
    const templateSelect = document.getElementById("id_content_template");
    const variablesInput = document.getElementById("id_variable_values");
    this.selectedTemplateId = templateSelect?.value || "";
    this.variableValues = variablesInput?.value || "";
  },

  get selectedTemplate() {
    return this.templates.find((template) => template.id === this.selectedTemplateId) || null;
  },

  get availableVariables() {
    if (!this.selectedTemplate) return [];
    const keys = new Set(Object.keys(this.selectedTemplate.variables || {}));
    for (const match of this.selectedTemplate.body.matchAll(/{{\s*(\d+)\s*}}/g)) {
      keys.add(match[1]);
    }
    return Array.from(keys)
      .sort((first, second) => Number(first) - Number(second))
      .map((key) => ({
        key,
        example: this.selectedTemplate.variables?.[key] || "Sem exemplo",
      }));
  },

  get parsedVariables() {
    return Object.fromEntries(
      this.variableValues
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const separator = line.indexOf("=");
          if (separator < 0) return [line, ""];
          return [line.slice(0, separator).trim(), line.slice(separator + 1).trim()];
        }),
    );
  },

  get previewBody() {
    if (!this.selectedTemplate) return "";
    return this.selectedTemplate.body.replace(/{{\s*(\d+)\s*}}/g, (placeholder, key) => (
      this.parsedVariables[key] || placeholder
    ));
  },

  variablePlaceholder(key) {
    return `{{${key}}}`;
  },
}));

Alpine.data("conversationChat", ({
  messagesElementId,
  csrfToken,
  pollInterval,
  listEndpoint,
  listPollInterval,
  conversationListUrl,
  settingsInitiallyOpen,
}) => ({
  body: "",
  customerServiceWindowOpen: false,
  channelFilter: "",
  chatPollError: "",
  error: "",
  endpoint: "",
  loadController: null,
  listPollController: null,
  listPollError: "",
  listPolling: false,
  listPollTimer: null,
  loading: false,
  messages: [],
  messagesEndpoint: "",
  mobileChatOpen: window.location.pathname !== new URL(conversationListUrl, window.location.origin).pathname,
  nextCursor: null,
  isOffline: !navigator.onLine,
  pollController: null,
  polling: false,
  pollTimer: null,
  readEndpoint: "",
  selectedContactName: "",
  selectedContactPhone: "",
  selectedChannelLabel: "",
  selectedId: "",
  selectedStatus: "",
  selectedStatusDisplay: "",
  selectedUrl: "",
  templateUrl: "",
  sending: false,
  showConversationSettings: settingsInitiallyOpen,
  unreadCounts: {},
  visibilityHandler: null,
  onlineHandler: null,
  offlineHandler: null,

  init() {
    const messagesElement = document.getElementById(messagesElementId);
    this.messages = messagesElement ? JSON.parse(messagesElement.textContent) : [];
    this.$root.querySelectorAll("[data-conversation-id]").forEach((link) => {
      this.unreadCounts[link.dataset.conversationId] = Number(link.dataset.unreadCount || 0);
    });
    const selectedLink = this.$root.querySelector("[data-conversation-id][data-selected='true']")
      || this.$root.querySelector("[data-conversation-id]");
    if (selectedLink) {
      this.applyConversation(selectedLink);
      this.nextCursor = this.messages.at(-1)?.id || null;
      this.markActiveConversationRead();
    }
    this.scrollToBottom();
    this.visibilityHandler = () => this.handleVisibilityChange();
    this.onlineHandler = () => this.handleOnline();
    this.offlineHandler = () => this.handleOffline();
    document.addEventListener("visibilitychange", this.visibilityHandler);
    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
    if (!document.hidden && navigator.onLine) this.resumePolling();
  },

  destroy() {
    this.suspendPolling();
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    window.removeEventListener("online", this.onlineHandler);
    window.removeEventListener("offline", this.offlineHandler);
  },

  handleVisibilityChange() {
    if (document.hidden) {
      this.suspendPolling();
      return;
    }
    if (navigator.onLine) this.resumePolling({ refreshImmediately: true });
  },

  handleOnline() {
    this.isOffline = false;
    if (!document.hidden) this.resumePolling({ refreshImmediately: true });
  },

  handleOffline() {
    this.isOffline = true;
    this.suspendPolling();
  },

  resumePolling({ refreshImmediately = false } = {}) {
    if (document.hidden || !navigator.onLine) return;
    this.startPolling();
    this.startListPolling();
    if (refreshImmediately) {
      this.pollActiveConversation();
      this.pollConversationList();
    }
  },

  suspendPolling() {
    this.stopPolling();
    this.stopListPolling();
  },

  applyConversation(link) {
    this.selectedId = link.dataset.conversationId;
    this.selectedContactName = link.dataset.contactName;
    this.selectedContactPhone = link.dataset.contactPhone;
    this.selectedChannelLabel = link.dataset.whatsappNumber
      ? `${link.dataset.whatsappConfigName} · ${link.dataset.whatsappNumber}`
      : "Sem número WhatsApp vinculado";
    this.selectedStatus = link.dataset.status;
    this.selectedStatusDisplay = link.dataset.statusDisplay;
    this.selectedUrl = link.href;
    this.messagesEndpoint = link.dataset.messagesEndpoint;
    this.endpoint = link.dataset.sendEndpoint;
    this.readEndpoint = link.dataset.readEndpoint;
    this.templateUrl = link.dataset.templateUrl;
    this.customerServiceWindowOpen = link.dataset.customerServiceWindowOpen === "true";

    this.$nextTick(() => {
      const status = document.getElementById("id_status");
      const assignedTo = document.getElementById("id_assigned_to");
      if (status) status.value = link.dataset.status;
      if (assignedTo) assignedTo.value = link.dataset.assignedToId;
    });
  },

  async selectConversation(link, { updateHistory = true } = {}) {
    if (link.dataset.conversationId === this.selectedId && this.messages.length) {
      this.mobileChatOpen = true;
      if (updateHistory) history.pushState({ conversationId: this.selectedId }, "", link.href);
      return;
    }

    this.error = "";
    this.loading = true;
    this.pollController?.abort();
    this.loadController?.abort();
    const controller = new AbortController();
    this.loadController = controller;

    try {
      const response = await fetch(link.dataset.messagesEndpoint, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok) {
        this.error = data.detail || "Não foi possível carregar a conversa.";
        return;
      }

      this.applyConversation(link);
      this.messages = data.items;
      this.nextCursor = data.next_cursor;
      this.mobileChatOpen = true;
      this.showConversationSettings = false;
      if (updateHistory) history.pushState({ conversationId: this.selectedId }, "", link.href);
      this.scrollToBottom();
      this.markActiveConversationRead();
    } catch (error) {
      if (error.name === "AbortError") return;
      this.error = "Não foi possível conectar ao servidor.";
    } finally {
      if (this.loadController === controller) this.loading = false;
    }
  },

  restoreConversationFromUrl() {
    const link = Array.from(this.$root.querySelectorAll("[data-conversation-id]"))
      .find((item) => item.href === window.location.href);
    if (link) {
      this.selectConversation(link, { updateHistory: false });
      return;
    }
    this.mobileChatOpen = false;
  },

  showConversationList() {
    this.mobileChatOpen = false;
    history.pushState({}, "", conversationListUrl);
  },

  startPolling() {
    this.stopPolling();
    this.pollTimer = window.setInterval(() => this.pollActiveConversation(), pollInterval);
  },

  stopPolling() {
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.pollController?.abort();
    this.pollController = null;
    this.polling = false;
  },

  startListPolling() {
    this.stopListPolling();
    this.listPollTimer = window.setInterval(
      () => this.pollConversationList(),
      listPollInterval,
    );
  },

  stopListPolling() {
    if (this.listPollTimer) window.clearInterval(this.listPollTimer);
    this.listPollTimer = null;
    this.listPollController?.abort();
    this.listPollController = null;
    this.listPolling = false;
  },

  async pollConversationList() {
    if (document.hidden || !navigator.onLine || this.listPolling) return;

    const controller = new AbortController();
    this.listPollController = controller;
    this.listPolling = true;

    try {
      const response = await fetch(listEndpoint, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Não foi possível atualizar a lista.");
      this.updateConversationList(data.items);
    } catch (error) {
      if (error.name !== "AbortError") this.listPollError = error.message;
    } finally {
      if (this.listPollController === controller) {
        this.listPollController = null;
        this.listPolling = false;
      }
    }
  },

  updateConversationList(items) {
    const container = this.$refs.conversationsList;
    if (!container) return;

    const links = new Map(
      Array.from(container.querySelectorAll("[data-conversation-id]"))
        .map((link) => [link.dataset.conversationId, link]),
    );
    const receivedIds = new Set(items.map((item) => item.id));
    this.updateGlobalUnreadBadge(
      items.reduce((total, item) => total + Number(item.unread_count || 0), 0),
    );

    if (items.some((item) => !links.has(item.id))) {
      window.location.reload();
      return;
    }

    links.forEach((link, id) => {
      if (!receivedIds.has(id)) {
        if (id === this.selectedId) {
          window.location.reload();
          return;
        }
        link.remove();
      }
    });

    items.forEach((item) => {
      const link = links.get(item.id);
      if (!link) return;

      this.unreadCounts[item.id] = item.id === this.selectedId ? 0 : item.unread_count;
      link.dataset.status = item.status;
      link.dataset.statusDisplay = item.status_display;
      link.dataset.assignedToId = item.assigned_to_id || "";
      link.dataset.whatsappConfigId = item.whatsapp_config_id || "";
      link.dataset.whatsappConfigName = item.whatsapp_config_name || "Sem setor";
      link.dataset.whatsappNumber = item.whatsapp_number || "";
      link.dataset.customerServiceWindowOpen = String(item.customer_service_window_open);
      if (item.id === this.selectedId) {
        this.selectedChannelLabel = item.whatsapp_number
          ? `${item.whatsapp_config_name} · ${item.whatsapp_number}`
          : "Sem número WhatsApp vinculado";
        this.customerServiceWindowOpen = item.customer_service_window_open;
      }

      const time = link.querySelector("[data-conversation-time]");
      if (time) time.textContent = this.formatConversationTime(item.last_message_at);

      const status = link.querySelector("[data-conversation-status]");
      if (status) {
        status.textContent = item.status_display;
        status.classList.remove(...this.allStatusClasses());
        status.classList.add(...this.statusClasses(item.status).split(" "));
      }

      const channel = link.querySelector("[data-conversation-channel]");
      if (channel) channel.textContent = item.whatsapp_config_name || "Sem setor";

      container.appendChild(link);
    });

    this.filterConversations();
    this.listPollError = "";
  },

  updateGlobalUnreadBadge(total) {
    const badge = document.querySelector("[data-global-unread-badge]");
    if (!badge) return;
    badge.textContent = total > 99 ? "99+" : String(total);
    badge.classList.toggle("hidden", total <= 0);
  },

  filterConversations() {
    this.$root.querySelectorAll("[data-conversation-id]").forEach((link) => {
      link.classList.toggle(
        "hidden",
        Boolean(this.channelFilter)
          && link.dataset.whatsappConfigId !== this.channelFilter,
      );
    });
  },

  formatConversationTime(value) {
    if (!value) return "";
    const date = new Date(value);
    const pad = (part) => String(part).padStart(2, "0");
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  },

  allStatusClasses() {
    return [
      "bg-gray-100", "text-gray-600", "dark:bg-white/5", "dark:text-gray-300",
      "bg-warning-50", "text-warning-600", "dark:bg-warning-500/10", "dark:text-warning-500",
      "bg-success-50", "text-success-500", "dark:bg-success-500/10",
    ];
  },

  async pollActiveConversation() {
    if (
      document.hidden
      || !navigator.onLine
      || !this.messagesEndpoint
      || this.polling
      || this.loading
    ) return;

    const conversationId = this.selectedId;
    const url = new URL(this.messagesEndpoint, window.location.origin);
    if (this.nextCursor) url.searchParams.set("after", this.nextCursor);
    const controller = new AbortController();
    this.pollController = controller;
    this.polling = true;

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Não foi possível atualizar a conversa.");
      if (conversationId !== this.selectedId) return;

      this.chatPollError = "";
      const knownIds = new Set(this.messages.map((message) => message.id));
      const newMessages = data.items.filter((message) => !knownIds.has(message.id));
      const shouldScroll = this.isNearMessagesEnd();
      this.messages.push(...newMessages);
      this.nextCursor = data.next_cursor;
      if (newMessages.length && shouldScroll) this.scrollToBottom();
      if (newMessages.some((message) => message.direction === "inbound")) {
        this.customerServiceWindowOpen = true;
        this.markActiveConversationRead();
      }
    } catch (error) {
      if (error.name !== "AbortError") this.chatPollError = error.message;
    } finally {
      if (this.pollController === controller) {
        this.pollController = null;
        this.polling = false;
      }
    }
  },

  isNearMessagesEnd() {
    const container = this.$refs.messagesContainer;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  },

  async markActiveConversationRead() {
    if (!this.readEndpoint) return;

    const conversationId = this.selectedId;
    try {
      const response = await fetch(this.readEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRFToken": csrfToken },
      });
      if (response.ok && conversationId === this.selectedId) {
        this.unreadCounts[conversationId] = 0;
      }
    } catch (_error) {
      return;
    }
  },

  statusClasses(status) {
    if (status === "closed") return "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300";
    if (status === "pending") return "bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-500";
    return "bg-success-50 text-success-500 dark:bg-success-500/10";
  },

  handleMessageKeydown(event) {
    if (event.isComposing || event.shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  },

  async sendMessage() {
    const body = this.body.trim();
    if (!body || this.sending || !this.customerServiceWindowOpen) {
      return;
    }

    this.error = "";
    this.sending = true;

    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ body }),
      });
      const data = await response.json();

      if (!response.ok) {
        this.error = data.detail || "Não foi possível enviar a mensagem.";
        return;
      }

      this.messages.push(data);
      this.body = "";
      this.scrollToBottom();
    } catch (_error) {
      this.error = "Não foi possível conectar ao servidor.";
    } finally {
      this.sending = false;
    }
  },

  scrollToBottom() {
    this.$nextTick(() => {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    });
  },
}));

Alpine.data("leadPipeline", ({ canMove, csrfToken }) => ({
  canMove,
  csrfToken,
  draggingLeadId: "",
  error: "",
  filtersOpen: false,
  overStageId: "",
  moving: false,

  startDrag(event, leadId) {
    if (!this.canMove || this.moving) {
      event.preventDefault();
      return;
    }

    this.error = "";
    this.draggingLeadId = leadId;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", leadId);
  },

  clearDrag() {
    this.draggingLeadId = "";
    this.overStageId = "";
  },

  setOverStage(stageId) {
    if (this.draggingLeadId) {
      this.overStageId = stageId;
    }
  },

  clearOverStage(stageId) {
    if (this.overStageId === stageId) {
      this.overStageId = "";
    }
  },

  moveSelected(leadId, stageId) {
    if (!stageId || this.moving) return;
    this.draggingLeadId = leadId;
    return this.moveLead(stageId);
  },

  async moveLead(stageId) {
    if (!this.canMove || !this.draggingLeadId || this.moving) {
      return;
    }

    const card = this.$root.querySelector(`[data-lead-card="${this.draggingLeadId}"]`);
    const sourceColumn = card?.closest("[data-stage-column]");
    if (!card || sourceColumn?.dataset.stageColumn === stageId) {
      this.clearDrag();
      return;
    }

    this.error = "";
    this.moving = true;
    try {
      const response = await fetch(card.dataset.moveEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": this.csrfToken,
        },
        body: JSON.stringify({ stage_id: stageId }),
      });
      const data = await response.json();

      if (!response.ok) {
        this.error = data.detail || "Não foi possível mover o lead.";
        return;
      }

      const targetColumn = this.$root.querySelector(`[data-stage-column="${data.stage_id}"]`);
      const targetList = targetColumn?.querySelector("[data-stage-leads]");
      if (!sourceColumn || !targetColumn || !targetList) {
        this.error = "O lead foi movido, mas não foi possível atualizar o pipeline.";
        return;
      }

      targetList.prepend(card);
      this.updateColumnStats(sourceColumn);
      this.updateColumnStats(targetColumn);
    } catch (_error) {
      this.error = "Não foi possível conectar ao servidor.";
    } finally {
      this.moving = false;
      this.clearDrag();
    }
  },

  updateColumnStats(column) {
    const cards = Array.from(column.querySelectorAll("[data-lead-card]"));
    const total = cards.reduce(
      (sum, card) => sum + Number(card.dataset.leadValue || 0),
      0,
    );
    const count = column.querySelector("[data-stage-count]");
    const totalElement = column.querySelector("[data-stage-total]");
    const emptyState = column.querySelector("[data-empty-stage]");

    if (count) count.textContent = `${cards.length} ${cards.length === 1 ? "lead" : "leads"}`;
    if (totalElement) totalElement.textContent = this.formatLeadValue(total);
    if (emptyState) emptyState.classList.toggle("hidden", cards.length > 0);
  },

  formatLeadValue(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  },
}));

window.Alpine = Alpine;
Alpine.start();
